/**
 * @author      Shivani Mehra
 * @date        July, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import { Container, Card, Row, Col, Form, Button, Badge, Alert, Spinner } from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jwt_decode from "jwt-decode";
import { FaFacebook, FaSave, FaPaperPlane, FaPlug, FaCog, FaCheckCircle, FaTrashAlt } from "react-icons/fa";

const MetaCapiSetting = () => {
  // Credentials & Mapping State
  const [pixelId, setPixelId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [testEventCode, setTestEventCode] = useState("");
  const [isActive, setIsActive] = useState(true);
  
  // Status Mapping
  const [qualifiedStatus, setQualifiedStatus] = useState("Closed - Converted");
  const [disqualifiedStatus, setDisqualifiedStatus] = useState("Closed - Not Converted");
  const [qualifiedEventName, setQualifiedEventName] = useState("QualifiedLead");
  const [disqualifiedEventName, setDisqualifiedEventName] = useState("DisqualifiedLead");

  // Test Events Panel State
  const [testEventName, setTestEventName] = useState("QualifiedLead");
  const [testLeadId, setTestLeadId] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [testCountryCode, setTestCountryCode] = useState("91");

  // UI state
  const [settingRecord, setSettingRecord] = useState(null);
  const [isSavingSetting, setIsSavingSetting] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);

  // Available Lead Statuses from Session Storage (standard codebase pattern)
  const [leadStatusOptions] = useState(() => {
    try {
      const parsed = JSON.parse(sessionStorage.getItem("lead_status"));
      if (Array.isArray(parsed)) {
        return parsed.map(item => typeof item === "string" ? item : (item.label || item.value || ""));
      }
    } catch (e) {
      console.error("Error reading lead status from session storage:", e);
    }
    return ["Open - Not Contacted", "Working Contacted", "Closed - Converted", "Closed - Not Converted"];
  });

  const [userInfo] = useState(() => {
    try {
      return jwt_decode(sessionStorage.getItem("token"));
    } catch (e) {
      return {};
    }
  });

  useEffect(() => {
    fetchMetaCapiSettings();
  }, []);

  const fetchMetaCapiSettings = async () => {
    try {
      const response = await WhatsAppAPI.getMetaCapiSetting();
      if (response.success && response.setting) {
        const s = response.setting;
        setSettingRecord(s);
        setPixelId(s.pixel_id || "");
        setAccessToken(s.access_token || "");
        setTestEventCode(s.test_event_code || "");
        setIsActive(s.is_active !== undefined ? s.is_active : true);
        setQualifiedStatus(s.qualified_status || "Closed - Converted");
        setDisqualifiedStatus(s.disqualified_status || "Closed - Not Converted");
        setQualifiedEventName(s.qualified_event_name || "QualifiedLead");
        setDisqualifiedEventName(s.disqualified_event_name || "DisqualifiedLead");
      }
    } catch (error) {
      console.error("Error fetching Meta CAPI settings:", error);
      toast.error("Failed to fetch Conversions API settings.");
    }
  };

  const handleSaveSetting = async (e) => {
    if (e) e.preventDefault();
    if (!pixelId.trim() || !accessToken.trim()) {
      toast.error("Pixel/Dataset ID and Access Token are required.");
      return;
    }

    setIsSavingSetting(true);
    try {
      const payload = {
        pixel_id: pixelId.trim(),
        access_token: accessToken.trim(),
        test_event_code: testEventCode.trim(),
        qualified_status: qualifiedStatus,
        disqualified_status: disqualifiedStatus,
        qualified_event_name: qualifiedEventName.trim(),
        disqualified_event_name: disqualifiedEventName.trim(),
        is_active: isActive
      };
      const response = await WhatsAppAPI.saveMetaCapiSetting(payload);
      if (response.success) {
        toast.success("Meta Conversions API settings saved successfully.");
        setSettingRecord(response.setting);
      } else {
        toast.error("Failed to save settings: " + (response.errors || "Unknown error"));
      }
    } catch (error) {
      console.error("Error saving Meta CAPI settings:", error);
      toast.error("Error saving settings.");
    } finally {
      setIsSavingSetting(false);
    }
  };

  const handleSendTestEvent = async (e) => {
    if (e) e.preventDefault();
    if (!pixelId.trim() || !accessToken.trim()) {
      toast.error("Please configure and save Pixel ID and Access Token first.");
      return;
    }
    if (!testEventName.trim()) {
      toast.error("Event Name is required.");
      return;
    }
    if (!testLeadId.trim() && !testEmail.trim() && !testPhone.trim()) {
      toast.error("Please provide at least one identifier (Lead ID, Email, or Phone).");
      return;
    }

    setIsSendingTest(true);
    try {
      const payload = {
        pixel_id: pixelId.trim(),
        access_token: accessToken.trim(),
        test_event_code: testEventCode.trim() || null,
        event_name: testEventName.trim(),
        lead_id: testLeadId.trim() || null,
        email: testEmail.trim() || null,
        phone: testPhone.trim() || null,
        country_code: testCountryCode
      };

      const response = await WhatsAppAPI.sendMetaCapiTestEvent(payload);
      if (response.success) {
        toast.success("Test event sent to Meta successfully!");
      } else {
        toast.error("Failed to send test event: " + (response.errors || "Meta API Error"));
      }
    } catch (error) {
      console.error("Error sending test event:", error);
      toast.error("Network or server error sending test event.");
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <>
      <Container className="mt-5 pb-5">
        {/* Quick Guide Banner */}
        <Row className="mx-5 mb-4">
          <Card className="p-4 header-bg shadow-sm border-0 rounded-3" style={{ background: "linear-gradient(135deg, #1877F2 0%, #1152B3 100%)", color: "#fff" }}>
            <div className="d-flex align-items-center gap-3">
              <FaFacebook size={36} className="text-white flex-shrink-0" />
              <div>
                <h5 className="mb-1 fw-bold">Meta Conversions API (CAPI) Integration</h5>
                <small className="text-white-50">
                  Update Meta in real-time when leads change status (e.g. Qualified or Disqualified). Optimize your Facebook Lead Ads campaigns using the <strong>Conversion Leads</strong> optimization model.
                </small>
              </div>
            </div>
          </Card>
        </Row>

        <Row className="mx-5">
          {/* Left Column: API Settings & Test Events */}
          <Col lg={6} className="mb-4">
            {/* Configuration Card */}
            <Card className="shadow-sm border-0 rounded-3 mb-4 card-glass">
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                    <FaCog /> Connection Settings
                  </h6>
                  <Badge bg={settingRecord && isActive ? "success" : "secondary"} className="p-2">
                    {settingRecord && isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>

                <Form onSubmit={handleSaveSetting}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Meta Pixel / Dataset ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 7663419707102663"
                      value={pixelId}
                      onChange={(e) => setPixelId(e.target.value)}
                      required
                    />
                    <Form.Text className="text-muted small">
                      Your 15-16 digit Meta Pixel or Dataset ID.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Conversions API Access Token</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Paste your System User Access Token here..."
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      required
                      style={{ resize: "none" }}
                    />
                    <Form.Text className="text-muted small">
                      Generate this token in Meta Events Manager &gt; Settings &gt; Conversions API &gt; Generate Access Token.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Test Event Code (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. TEST12345"
                      value={testEventCode}
                      onChange={(e) => setTestEventCode(e.target.value)}
                    />
                    <Form.Text className="text-muted small">
                      Provide this to see test logs in Meta Events Manager &gt; Test Events tab. Remove it for live traffic.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="capi-active-switch"
                      label="Enable Conversions API Sync"
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      className="fw-semibold text-secondary"
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button variant="primary" type="submit" disabled={isSavingSetting} className="btn-premium">
                      {isSavingSetting ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save Configuration</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>

            {/* Test Panel Card */}
            <Card className="shadow-sm border-0 rounded-3 card-glass">
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-3 d-flex align-items-center gap-2">
                  <FaPaperPlane /> Test Event Console
                </h6>
                <p className="text-muted small">
                  Simulate a lead status conversion event and verify that Meta receives the data correctly.
                </p>

                <Form onSubmit={handleSendTestEvent}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Event Name</Form.Label>
                    <Form.Select
                      value={testEventName}
                      onChange={(e) => setTestEventName(e.target.value)}
                    >
                      <option value="QualifiedLead">QualifiedLead</option>
                      <option value="DisqualifiedLead">DisqualifiedLead</option>
                      <option value="Lead">Lead</option>
                      <option value="Contact">Contact</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-bold">Meta Lead ID (leadgen_id)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g. 122321841248218169"
                      value={testLeadId}
                      onChange={(e) => setTestLeadId(e.target.value)}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Code</Form.Label>
                        <Form.Control
                          type="text"
                          value={testCountryCode}
                          onChange={(e) => setTestCountryCode(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-bold">Phone Number</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. 9876543210"
                          value={testPhone}
                          onChange={(e) => setTestPhone(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label className="small fw-bold">Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="e.g. testlead@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button variant="outline-primary" type="submit" disabled={isSendingTest}>
                      {isSendingTest ? <Spinner animation="border" size="sm" /> : <><FaPaperPlane className="me-2" /> Send Test Event</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Column: Lead Status mapping */}
          <Col lg={6} className="mb-4">
            <Card className="shadow-sm border-0 rounded-3 card-glass">
              <Card.Body className="p-4">
                <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
                  <FaPlug /> Pipeline Stage Mapping
                </h6>
                <p className="text-muted small mb-4">
                  Map your CRM's local lead statuses to Meta CAPI standard/custom event names. When a lead's status changes to a mapped value, the webhook or update event triggers the CAPI request.
                </p>

                <Alert variant="info" className="small border-0 mb-4">
                  <h6 className="alert-heading fw-bold mb-1"><FaCheckCircle className="me-1" /> How it works:</h6>
                  When a lead with a valid <strong>Meta Lead ID</strong> has its status changed in the Lead Manager, WatConnect checks these mappings. If it matches, the status change event is posted to Meta automatically.
                </Alert>

                <Form onSubmit={handleSaveSetting}>
                  <Card className="p-3 mb-3 bg-light border-0">
                    <h6 className="fw-semibold text-primary mb-3">1. Qualified Lead Stage</h6>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Qualified Status Trigger</Form.Label>
                      <Form.Select
                        value={qualifiedStatus}
                        onChange={(e) => setQualifiedStatus(e.target.value)}
                      >
                        {leadStatusOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Choose the status that means a lead is "Qualified".
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label className="small fw-bold">Meta Event Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={qualifiedEventName}
                        onChange={(e) => setQualifiedEventName(e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted small">
                        The event name sent to Meta (e.g. <code>QualifiedLead</code> or <code>Lead</code>).
                      </Form.Text>
                    </Form.Group>
                  </Card>

                  <Card className="p-3 mb-4 bg-light border-0">
                    <h6 className="fw-semibold text-danger mb-3">2. Disqualified Lead Stage</h6>
                    
                    <Form.Group className="mb-3">
                      <Form.Label className="small fw-bold">Disqualified Status Trigger</Form.Label>
                      <Form.Select
                        value={disqualifiedStatus}
                        onChange={(e) => setDisqualifiedStatus(e.target.value)}
                      >
                        {leadStatusOptions.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted small">
                        Choose the status that means a lead is "Disqualified".
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-0">
                      <Form.Label className="small fw-bold">Meta Event Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={disqualifiedEventName}
                        onChange={(e) => setDisqualifiedEventName(e.target.value)}
                        required
                      />
                      <Form.Text className="text-muted small">
                        The event name sent to Meta (e.g. <code>DisqualifiedLead</code>).
                      </Form.Text>
                    </Form.Group>
                  </Card>

                  <div className="d-grid">
                    <Button variant="success" type="submit" disabled={isSavingSetting}>
                      {isSavingSetting ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" /> Save Pipeline Mappings</>}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ToastContainer autoClose={5000} />
    </>
  );
};

export default MetaCapiSetting;
